import styled from "@emotion/styled";
import { Box, Switch, CircularProgress, Alert, Typography, FormControl, Select, MenuItem } from "@mui/material";
import { useMutation, useQuery, keepPreviousData } from "@tanstack/react-query";
import React, { useEffect, useState, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import Cookies from "universal-cookie";
import {
  getWithQueryApiServices,
  putWithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { securityProfileFormInitVal } from "../../../constants";
import { SplitContainer, StyledHeader } from "../../../styles/commonStyles";
import { errorToast, successToast } from "../../../utils/helper";
import { FullFeaturedCrudGrid } from "../../shared/CommonTable";
import { ParentPageWrapper } from "../../shared/ParentPageWrapper";
import { SecurityProfileForm } from "./SecurityProfileForm";
import { usePermissions } from "../../../hooks/usePermissions";
import { LoadingScreen } from "../../shared/LoadingScreen";
import { PermissionDenied } from "../../shared/PermissionDenied";

const BoxContainer = styled(Box)(({ theme }) => ({
  padding: "10px",
  [theme.breakpoints.up("sm")]: {
    padding: "24px",
  },
  [theme.breakpoints.up("md")]: {
    padding: "1%",
  },
  boxSizing: "border-box",
}));

const ParentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "stretch",
  flexWrap: "wrap",
  gap: "20px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: "30px",
  },
}));

const ResponsiveSplitContainer = styled(SplitContainer)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    width: "100% !important",
  },
}));

const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "300px",
  flexDirection: "column",
  gap: "16px",
});

const DisabledPaneOverlay = styled("div")(({ disabled }) => ({
  position: "relative",
  height: "100%",
  transition: "all 0.3s ease",
  pointerEvents: "auto",
  "&::after": disabled ? {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.01)",
    pointerEvents: "none",
    zIndex: 1,
    borderRadius: 10,
  } : {},
}));

const Security2FAEditCell = ({ params, setOuterValue }) => {
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (params.hasFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [params.hasFocus]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
      <Switch
        inputRef={inputRef}
        checked={!!params.value}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.stopPropagation(); // Stop DataGrid from committing edit mode on Enter
            const isChecked = !params.value;
            params.api.setEditCellValue({ id: params.id, field: params.field, value: isChecked ? 1 : 0 });
            if (params.row.isNew) {
              setOuterValue({
                is2FA: isChecked ? 1 : 0,
                isNew: true,
                id: params.row.id,
              });
            }
          }
        }}
        onChange={(e) => {
          const isChecked = e.target.checked;
          params.api.setEditCellValue({ id: params.id, field: params.field, value: isChecked ? 1 : 0 });
          if (params.row.isNew) {
            setOuterValue({
              is2FA: isChecked ? 1 : 0,
              isNew: true,
              id: params.row.id,
            });
          }
        }}
        size="small"
      />
    </Box>
  );
};

export const SecurityProfile = () => {
  const outletContext = useOutletContext();
  const [searchSecurityProfile, setSearchSecurityProfile] = useState("");
  const [debouncedSearchSecurityProfile, setDebouncedSearchSecurityProfile] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchSecurityProfile(searchSecurityProfile);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchSecurityProfile]);

  const [selectedSecurityProfiles, setSelectedSecurityProfiles] = useState([]);
  const [securityProfileData, setSecurityProfileData] = useState({});
  const [isFormEnabled, setIsFormEnabled] = useState(false);
  const [outerValue, setOuterValue] = useState(null);
  const [isEditingGrid, setIsEditingGrid] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [pendingSelectProfileId, setPendingSelectProfileId] = useState(null);

  // ✅ Strict Granular Permissions
  const { hasPermission, isGlobalSuperAdmin } = usePermissions();
  const canRead = hasPermission("Security: Read");
  const canCreate = hasPermission("Security: Create");
  const canUpdate = hasPermission("Security: Update");
  const canDelete = hasPermission("Security: Delete");
  
  // For UI alerts and disabling the grid entirely if no write access
  const hasAnyWriteAccess = canCreate || canUpdate || canDelete;

  const handleGlobalCompanyChange = (e) => {
    if (setCurrentCompany) setCurrentCompany(e.target.value);
  };

  const { currentCompany, fullCompanyList, tenantString, setCurrentCompany, isCompanySwitching } = outletContext || {};

  const effectiveTenant = useMemo(() => {
    const { currentTenant, user } = outletContext || {};
    const cookies = new Cookies();
    if (currentTenant && currentTenant !== "") {
      return currentTenant;
    }

    // 2. Local session recovery (Existing logins)
    const dbCookie = cookies.get("db");
    const tenantIdCookie = cookies.get("tenantId");
    
    if (dbCookie && dbCookie !== "") return dbCookie;

    // 3. Fallback to user data from context or Redux
    const sessionData = user || {};
    
    // Check for explicit database name (binding string)
    if (sessionData.db && sessionData.db !== "") return sessionData.db;

    // Check for numerical tenant ID (Check both casings and cookies)
    const tid = sessionData.tenantID || sessionData.tenantId || tenantIdCookie;
    if (tid) {
      return String(tid);
    }

    return null;
  }, [outletContext]);

  const columns = [
    { 
      field: "name", 
      headerName: "Name", 
      flex: 1, 
      editable: canUpdate || isGlobalSuperAdmin,
      pattern: /^(?!(None|From Anywhere)$)/i,
      patternError: 'The names "None" and "From Anywhere" are reserved system profiles and cannot be used.'
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      editable: canUpdate || isGlobalSuperAdmin,
    },
    {
      field: "is2FA",
      headerName: "2FA Enable",
      flex: 0.5,
      type: "boolean",
      editable: canUpdate || isGlobalSuperAdmin,
      renderCell: (params) => {
        return (
          <Switch
            onChange={(e) => {
              if (params.row.isNew) {
                setOuterValue({
                  is2FA: e.target.checked ? 1 : 0,
                  isNew: true,
                  id: params.row.id,
                });
              } else {
                updateSecurityProfileDetails.mutate({
                  ...params.row,
                  is2FA: e.target.checked ? 1 : 0,
                });
              }
            }}
            checked={!!params.value}
            size="small"
            disabled={!canUpdate && !isGlobalSuperAdmin}
          />
        );
      },
      renderEditCell: (params) => (
        <Security2FAEditCell params={params} setOuterValue={setOuterValue} />
      )
    },
  ];

  const handleSecurityProfileCheckbox = (item) => {
    let copyValue = [...selectedSecurityProfiles];

    if (copyValue.some((profile) => profile?.id === item?.id)) {
      copyValue = copyValue.filter((val) => val?.id !== item?.id);
    } else {
      copyValue = [item];
    }

    setSelectedSecurityProfiles([...copyValue]);

    if (copyValue?.length === 1 && !copyValue[0]?.isNew) {
      setSecurityProfileData(copyValue?.[0]);
      setIsFormEnabled(true);
    } else {
      setSecurityProfileData(copyValue?.length === 1 ? copyValue[0] : {});
      setIsFormEnabled(false);
    }
  };

  const buildQueryString = () => {
    const params = new URLSearchParams();
    params.append('pageNumber', (paginationModel.page + 1).toString());
    params.append('pageSize', paginationModel.pageSize.toString());
    
    if (effectiveTenant) {
      params.append('selectedTenant', effectiveTenant);
    }
    
    params.append('search', debouncedSearchSecurityProfile || '');
    
    if (outletContext?.tenantString) {
      const cleanTenantString = outletContext.tenantString.startsWith('&') ? 
        outletContext.tenantString.slice(1) : outletContext.tenantString;
      return `${params.toString()}&${cleanTenantString}`;
    }
    
    return params.toString();
  };

  const { 
    data: securityProfilesData, 
    refetch: securityProfileListRefetch,
    isLoading,
    error,
    isFetching
  } = useQuery({
    queryKey: [
      "securityProfileLists",
      effectiveTenant,
      debouncedSearchSecurityProfile,
      outletContext?.tenantString,
      paginationModel,
    ],
    queryFn: async () => {
      const queryString = buildQueryString();
      const response = await getWithQueryApiServices(
        apiRoutes.GET_SECURITY_PROFILE_LIST,
        queryString
      );
      return response;
    },
    enabled: !!effectiveTenant,
    placeholderData: keepPreviousData,
    retry: 3,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      errorToast('Failed to load security profiles');
    },
  });

  // Reset page to 0 when search filter or tenant changes
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchSecurityProfile, effectiveTenant]);

  useEffect(() => {
    if (pendingSelectProfileId && securityProfilesData?.data?.list) {
      const newProfile = securityProfilesData.data.list.find(
        (p) => Number(p.securityprofileID) === Number(pendingSelectProfileId)
      );
      if (newProfile) {
        const selectedRow = {
          ...newProfile,
          id: newProfile.securityprofileID
        };
        setSelectedSecurityProfiles([selectedRow]);
        setSecurityProfileData(selectedRow);
        setIsFormEnabled(true);
        setPendingSelectProfileId(null);
      }
    }
  }, [securityProfilesData, pendingSelectProfileId]);

  const handleGridSuccess = (dataOrRow) => {
    const newId = dataOrRow?.insertId || dataOrRow?.securityprofileID;
    if (newId) {
      setPendingSelectProfileId(newId);
    } else if (dataOrRow) {
      const updatedRow = {
        ...dataOrRow,
        id: dataOrRow.id || dataOrRow.securityprofileID
      };
      setSelectedSecurityProfiles([updatedRow]);
      setSecurityProfileData(updatedRow);
      setIsFormEnabled(true);
    }
  };

  const updateSecurityProfileDetails = useMutation({
    mutationKey: ["updateuserDetails"],
    mutationFn: (user) => {
      const queryString = effectiveTenant ? `selectedTenant=${effectiveTenant}` : '';
      return putWithQueryApiServices(
        apiRoutes.UPDATE_SECURITY_PROFILE,
        queryString,
        user
      );
    },
    onSuccess: () => {
      securityProfileListRefetch();
      successToast("Security Profile Updated Successfully");
    },
    onError: (error) => {
      errorToast(error.response?.data || "Failed to update security profile");
    },
  });

  useEffect(() => {
    setIsFormEnabled(false);
    setSelectedSecurityProfiles([]);
    setSearchSecurityProfile("");
    setSecurityProfileData({});
  }, [effectiveTenant]);

  useEffect(() => {
    if (effectiveTenant && !isLoading && !isFetching) {
      securityProfileListRefetch();
    }
  }, [effectiveTenant]);

  const profilesList = securityProfilesData?.data?.list || [];
  const hasProfiles = profilesList.length > 0;

  // Final visibility logic: If we have profiles or have loaded once, show them. Don't wait for metadata.
  if (hasProfiles || !!securityProfilesData) {
    // Continue to normal render
  } else if (!effectiveTenant) {
     return (
      <ParentPageWrapper>
        <BoxContainer>
          <StyledHeader>Security Profiles</StyledHeader>
          <LoadingContainer>
            <CircularProgress />
            <Alert severity="warning">
              Waiting for tenant information...
            </Alert>
          </LoadingContainer>
        </BoxContainer>
      </ParentPageWrapper>
    );
  } else if ((isLoading || isFetching) && !isCompanySwitching) {
    return (
      <ParentPageWrapper>
        <BoxContainer>
          <StyledHeader>Security Profiles</StyledHeader>
          <LoadingScreen message="Loading security profiles..." />
        </BoxContainer>
      </ParentPageWrapper>
    );
  }

  if (error && !hasProfiles) {
    return (
      <ParentPageWrapper>
        <BoxContainer>
          <StyledHeader>Security Profiles</StyledHeader>
          <LoadingContainer>
            <Alert 
              severity="error"
              action={
                <button 
                  onClick={() => securityProfileListRefetch()}
                  style={{ 
                    padding: '5px 10px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Retry
                </button>
              }
            >
              Failed to load security profiles: {error.message}
            </Alert>
          </LoadingContainer>
        </BoxContainer>
      </ParentPageWrapper>
    );
  }

  if (!canRead && !isGlobalSuperAdmin) {
    return (
      <ParentPageWrapper>
        <PermissionDenied message="You do not have permission to view Security Profiles. Please contact your administrator." />
      </ParentPageWrapper>
    );
  }

  return (
    <ParentPageWrapper>
      <BoxContainer>
        <StyledHeader>Security Profiles</StyledHeader>

        {!hasAnyWriteAccess && !isGlobalSuperAdmin && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Read-Only Mode:</strong> You can view these security profiles, but changes are restricted based on your permissions.
          </Alert>
        )}

        <ParentContainer>
          <ResponsiveSplitContainer
            style={{
              width: "48%",
              backgroundColor: "transparent",
            }}
            sx={{
              maxHeight: { xs: "none", md: "84svh" },
              height: { xs: "auto", md: "100svh" },
              overflow: { xs: "visible", md: "hidden" },
            }}
          >
             <DisabledPaneOverlay disabled={!canRead && !isGlobalSuperAdmin}>
              <div style={{ backgroundColor: "#FFFFFF", borderRadius: "10px", padding: "20px", height: "92%", overflow: "hidden" }}>
                <FullFeaturedCrudGrid
                  initialRows={profilesList}
                  columns={columns}
                  label="securityprofile"
                  handleSelection={handleSecurityProfileCheckbox}
                  search={searchSecurityProfile}
                  setSearch={setSearchSecurityProfile}
                  apiUrl={apiRoutes.CREATE_SECURITY_PROFILE}
                  refetch={securityProfileListRefetch}
                  initialFormValue={securityProfileFormInitVal}
                  refreshGridTrigger={outerValue}
                  setOuterValue={setOuterValue}
                  // Interaction disabled if NOT authorized OR editing is taking place elsewhere
                  disabled={!canRead && !isGlobalSuperAdmin}
                  isEditing={isEditingGrid}
                  onEditingStateChange={setIsEditingGrid}
                  onCancelEdit={() => {
                    setIsEditingGrid(false);
                    setSelectedSecurityProfiles([]);
                    setSecurityProfileData({});
                    setIsFormEnabled(false);
                  }}
                  onSuccess={handleGridSuccess}
                  selectedRowId={selectedSecurityProfiles?.[0]?.securityprofileID || null}
                  canCreate={canCreate}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                  permission={isGlobalSuperAdmin} // Legacy fallback
                  hideFooterPagination={true}
                  rowCount={securityProfilesData?.data?.count || 0}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  loading={isFetching}
                />
              </div>
            </DisabledPaneOverlay>
          </ResponsiveSplitContainer>
          <ResponsiveSplitContainer
            style={{
              width: "48%",
              backgroundColor: "transparent",
            }}
            sx={{
              height: { xs: "auto", md: "100svh" },
              maxHeight: { xs: "none", md: "84svh" },
            }}
          >
            {/* Blur only if NOT authorized */}
            <DisabledPaneOverlay disabled={!canRead && !isGlobalSuperAdmin}>
              <SecurityProfileForm
                securityProfileData={securityProfileData}
                // Interaction disabled if NOT authorized
                isFormEnabled={isFormEnabled && !isEditingGrid && (canUpdate || isGlobalSuperAdmin)}
                setIsFormEnabled={setIsFormEnabled}
                setSelectedSecurityProfiles={setSelectedSecurityProfiles}
                effectiveTenant={effectiveTenant}
                tenantString={outletContext?.tenantString}
              />
            </DisabledPaneOverlay>
          </ResponsiveSplitContainer>
        </ParentContainer>
      </BoxContainer>
    </ParentPageWrapper>
  );
};